/**
 * Read NDJSON events from a streaming response.
 * Generator function for memory efficiency with large streams.
 *
 * @param response - The streaming HTTP response
 * @param abortSignal - Optional signal to abort the stream (e.g., on component unmount)
 */
export async function* readStreamEvents(
  response: Response,
  abortSignal?: AbortSignal
): AsyncGenerator<any, void, unknown> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("Response body has no reader");
  }

  // When abort is signaled, cancel the reader to unblock reader.read()
  const abortHandler = () => {
    reader.cancel().catch((err) => {
      console.warn("Failed to cancel stream reader:", err);
    });
  };
  abortSignal?.addEventListener("abort", abortHandler);

  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed) {
          try {
            yield JSON.parse(trimmed);
          } catch {
            console.warn("Failed to parse stream line:", trimmed);
          }
        }
      }
    }

    // Process remaining buffer
    if (buffer.trim()) {
      try {
        yield JSON.parse(buffer.trim());
      } catch {
        console.warn("Failed to parse final buffer:", buffer);
      }
    }
  } finally {
    abortSignal?.removeEventListener("abort", abortHandler);
    reader.releaseLock();
  }
}
